import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import CategoryCard from "../CategoryCard";
import SinglePropSelectModal from "../SinglePropSelectModal";
import MultiPropSelectModal from "../MultiPropSelectModal";
import { BadgeGroup, ImpactText, ModalHeader, PrimaryAction, RecommendedBadge, ReplicateContent, ReplicateHeader, ReplicateSection, ReplicateSelectionCard, ReplicateSubtitle, ReplicateTitle } from "../Styles/InputStyles";

const OnboardingSettingsModal = ({
  onClose,
  settingsData,
  editSettingsMutate,
  isLoading,
  property,
  propertyList,
  fetchSettingsLoading,
  isDataLoaded,
  propertyParam,
  fetchOnboardingSettingsMutate,
  editSettingsLoading,
  selectedProperties,
  setSelectedProperties
}) => {
  const [settings, setSettings] = useState({
    aadhaarOtpVerification: false,
    policeVerification: false,
    onlinePayment: false,
    rentAgreement: false,
    rentalBond: false,
    skipKYC: false,
  });

  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  // const [selectedProperties, setSelectedProperties] = useState([]);
  const [isPropertyChanging, setIsPropertyChanging] = useState(false);
  const allProperties = propertyList || [];


  useEffect(() => {
    if (propertyParam && filteredProperties.length > 0) {
      const propertyFromParam = filteredProperties.find(prop => prop.id === propertyParam);
      if (propertyFromParam && propertyFromParam.id !== selectedPropertyId) {
        setSelectedProperty(propertyFromParam);
        setSelectedPropertyId(propertyFromParam.id);
      }
    }
  }, [propertyParam, filteredProperties]);

  useEffect(() => {
    if (propertyList) {
      const filtered = propertyList?.filter(prop => prop.id !== 0) || [];
      setFilteredProperties(filtered);

      if (filtered.length > 0 && !selectedProperty) {
        let initialProperty;

        if (propertyParam && propertyParam !== 0) {
          initialProperty = filtered.find(prop => prop.id === propertyParam);
        }

        if (!initialProperty) {
          initialProperty = filtered[0];
        }

        setSelectedProperty(initialProperty);
        setSelectedPropertyId(initialProperty.id);

        if (fetchOnboardingSettingsMutate && initialProperty.id) {
          fetchOnboardingSettingsMutate(initialProperty.id);
        }
      }
    }
  }, [propertyList, propertyParam, fetchOnboardingSettingsMutate, selectedProperty]);

  useEffect(() => {
    if (settingsData) {
      setSettings({
        aadhaarOtpVerification: settingsData?.isIdVerificationEnabled === 2,
        policeVerification: settingsData?.isPoliceVerificationEnabled === 1,
        onlinePayment: settingsData?.isOnlinePaymentEnabled === 1,
        rentAgreement: settingsData?.isRentAgreementEnabled === 1,
        skipKYC: settingsData?.isSkipKycEnabled === 1,
      });
    }
  }, [settingsData]);
  const toggleSetting = (key) => {
    const newValue = !settings[key];

    const updatedSettings = {
      ...settings,
      [key]: newValue
    };

    setSettings(updatedSettings);

    const payload = {
      propIds: [selectedPropertyId],
      ekyc: updatedSettings.aadhaarOtpVerification ? 2 : 0,
      onlinePayment: updatedSettings.onlinePayment ? 1 : 0,
      policeVerification: updatedSettings.policeVerification ? 1 : 0,
      rentAgreement: updatedSettings.rentAgreement ? 1 : 0,
      skipKyc: updatedSettings.skipKYC ? 1 : 0,
      replicate: 0,
      selectedPropertyId: selectedPropertyId
    };

    editSettingsMutate(payload);
  };

  const handleSelectedProperty = (selectedOption) => {
    const newPropertyId = selectedOption.id;

    if (newPropertyId === selectedPropertyId) {
      setSelectedProperty(selectedOption);
      return;
    }

    setIsPropertyChanging(true);
    setSelectedProperty(selectedOption);
    setSelectedPropertyId(newPropertyId);

    setSettings({
      aadhaarOtpVerification: false,
      policeVerification: false,
      onlinePayment: false,
      rentAgreement: false,
      skipKYC: false,
    });

    if (fetchOnboardingSettingsMutate) {
      fetchOnboardingSettingsMutate(newPropertyId, {
        onSettled: () => {
          setIsPropertyChanging(false);
        }
      });
    } else {
      setIsPropertyChanging(false);
    }
  };
  const handleReplicateSettings = () => {
    if (selectedProperties.length === 0) {
      setShowMultiSelectModal(true);
      return;
    }

    const payload = {
      propIds: selectedProperties,
      ekyc: settings.aadhaarOtpVerification ? 2 : 0,
      onlinePayment: settings.onlinePayment ? 1 : 0,
      policeVerification: settings.policeVerification ? 1 : 0,
      rentAgreement: settings.rentAgreement ? 1 : 0,
      skipKyc: settings.skipKYC ? 1 : 0,
      replicate: 1,
      selectedPropertyId: selectedPropertyId
    };

    editSettingsMutate(payload);
  };

  const handleMultiPropertySave = (selectedProps) => {
    setSelectedProperties(selectedProps);
    setShowMultiSelectModal(false);
  };

  const renderSelectedProperties = (selected = []) => {
    if (!selected.length) return "No properties selected";

    if (selected.length === allProperties.length) {
      return "All Properties";
    }

    const selectedPropertyNames = allProperties
      .filter((prop) => selected.includes(prop.id))
      .map((prop) => prop.name);

    if (!selectedPropertyNames.length) return "No properties selected";

    const maxToShow = 2;
    const namesToShow = selectedPropertyNames.slice(0, maxToShow);
    const remainingCount = selectedPropertyNames.length - maxToShow;

    return (
      <PropertyDisplayContainer>
        {namesToShow.join(", ")}
        {remainingCount > 0 && (
          <RemainingCount>+{remainingCount}</RemainingCount>
        )}
      </PropertyDisplayContainer>
    );
  };


  const showLoading = fetchSettingsLoading || !isDataLoaded || isPropertyChanging;

  return (
    <div className="bg-white w-full h-full flex flex-col">
      <ModalHeader>
        <HeaderTitle>Onboarding Settings</HeaderTitle>
        <CloseButton onClick={onClose}>
          <MdClose color="white" size={24} />
        </CloseButton>
      </ModalHeader>

      {showLoading ? (
        <div className="flex items-center justify-center h-full">
          <CircularProgress />
        </div>
      ) : (
        <>
          <ContentContainer>
            <ContentWrapper>
              <Section>
                {/* Show property selector only when "All Properties" is selected in main dropdown */}
                {(property?.id === 0 || propertyParam === 0) && filteredProperties.length > 0 && (
                  // <PropertySection>
                  <CategoryCard
                    title="Property"
                    selectedCategory={selectedProperty}
                    onClick={() => setShowPropertyModal(true)}
                  />
                  // </PropertySection>
                )}

                <SettingsGrid>
                  {/* <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>E-KYC</SettingTitle>
                        <SettingDescription>
                          Verify tenant identity securely and streamline the onboarding process.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.aadhaarOtpVerification}
                        onChange={() => toggleSetting("aadhaarOtpVerification")}
                        disabled={isLoading || isPropertyChanging}
                      />
                    </SettingContent>
                  </SettingCard> */}
                  {/* <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Skip KYC</SettingTitle>
                        <SettingDescription>
                          Allow tenants to skip KYC verification process.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.skipKYC}
                        onChange={() => toggleSetting("skipKYC")}
                        disabled={isLoading || isPropertyChanging}
                      />
                    </SettingContent>
                  </SettingCard>
                  <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Online Payment</SettingTitle>
                        <SettingDescription>
                          Allow tenants to make secure and hassle-free online payments.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.onlinePayment}
                        onChange={() => toggleSetting("onlinePayment")}
                        disabled={isLoading || isPropertyChanging}
                      />
                    </SettingContent>
                  </SettingCard> */}

                  <div className="mb-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Verification & Collection Settings
                    </p>
                  </div>


                  <SettingCard $variant="success" $active={settings.aadhaarOtpVerification}>

                    <SettingContent>

                      {/* LEFT CONTENT */}
                      <SettingHeader>

                        <TitleRow>
                          <SettingTitle>E-KYC Verification</SettingTitle>
                          {!settings.aadhaarOtpVerification && (
                            <RecommendedBadge>Recommended</RecommendedBadge>
                          )}
                          {settings.aadhaarOtpVerification && (
                            <StatusBadge $variant="success">Enabled</StatusBadge>
                          )}
                        </TitleRow>

                        <SettingDescription>
                          Verify tenant identity securely using DigiLocker for faster and paperless onboarding.
                        </SettingDescription>

                        <ImpactText className="text-green-600">
                          ↑ Reduces fraud risk and improves tenant trust
                        </ImpactText>
                        {/* {!settings.aadhaarOtpVerification && (
                          <HelperText>
                            Recommended to enable for safer tenant verification and reduced fraud risk.
                          </HelperText>
                        )} */}

                      </SettingHeader>

                      {/* RIGHT CONTROL */}
                      <ToggleWrapper>
                        <Toggle
                          checked={settings.aadhaarOtpVerification}
                          onChange={() => toggleSetting("aadhaarOtpVerification")}
                          disabled={isLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>

                    </SettingContent>

                  </SettingCard>

                  <SettingCard $variant="warning" $active={settings.skipKYC}>

                    <SettingContent>

                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>Allow Skip KYC</SettingTitle>

                          {/* {settings.skipKYC && (
                            <StatusBadge $variant="warning">Enabled</StatusBadge>
                          )} */}
                          <BadgeGroup>
                            {!settings.skipKYC && (
                              <RecommendedBadge>Recommended</RecommendedBadge>
                            )}

                            {settings.skipKYC && (
                              <StatusBadge $variant="warning">Enabled</StatusBadge>
                            )}
                          </BadgeGroup>
                        </TitleRow>

                        <SettingDescription>
                          Tenants can bypass identity verification. This may increase risk of fraud.
                        </SettingDescription>

                        <ImpactText className="text-red-500">
                          ↓ Increases fraud risk and reduces tenant accountability
                        </ImpactText>

                        {!settings.skipKYC && (
                          <HelperText>
                            Recommended to keep this disabled for better security.
                          </HelperText>
                        )}
                      </SettingHeader>

                      <ToggleWrapper>
                        <Toggle
                          checked={settings.skipKYC}
                          onChange={() => toggleSetting("skipKYC")}
                          disabled={isLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>

                    </SettingContent>

                  </SettingCard>

                  <SettingCard $variant="success" $active={settings.onlinePayment}>

                    <SettingContent>

                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>Online Payments</SettingTitle>

                          <BadgeGroup>
                            {!settings.onlinePayment && (
                              <RecommendedBadge>Recommended</RecommendedBadge>
                            )}

                            {settings.onlinePayment && (
                              <StatusBadge $variant="success">Enabled</StatusBadge>
                            )}
                          </BadgeGroup>
                        </TitleRow>

                        <SettingDescription>
                          Accept rent and other payments online for faster and hassle-free collections.
                        </SettingDescription>

                        <ImpactText className="text-green-600">
                          ↑ Improves on-time payments and reduces manual effort
                        </ImpactText>

                        {/* {!settings.onlinePayment && (
                          <HelperText>
                            Enable to collect payments digitally and reduce manual tracking.
                          </HelperText>
                        )} */}
                      </SettingHeader>

                      <ToggleWrapper>
                        <Toggle
                          checked={settings.onlinePayment}
                          onChange={() => toggleSetting("onlinePayment")}
                          disabled={isLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>

                    </SettingContent>

                  </SettingCard>

                  {/* <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Rent Agreement</SettingTitle>
                        <SettingDescription>
                          Allow tenant to view and accept rent agreement
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.rentAgreement}
                        onChange={() => toggleSetting("rentAgreement")}
                        disabled={isLoading || isPropertyChanging}
                      />
                    </SettingContent>
                  </SettingCard> */}
                  {/* <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Police Verification</SettingTitle>
                        <SettingDescription>
                          Enable this to conduct a thorough police verification process.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.policeVerification}
                        onChange={() => toggleSetting("policeVerification")}
                        disabled={isLoading || isPropertyChanging}
                      />
                    </SettingContent>
                  </SettingCard> */}
                </SettingsGrid>

                {/* Replicate Settings Section - Only show when multiple properties exist */}
                {filteredProperties.length > 1 && (
                  // <ReplicateSection>
                  //   <ReplicateHeader>
                  //     <ReplicateTitle>Replicate Settings to Other Properties</ReplicateTitle>
                  //   </ReplicateHeader>

                  //   <ReplicateContent>
                  //     <PropertiesSelection>
                  //       <CategoryCard
                  //         type="button"
                  //         selectedCategory={selectedProperties.length > 0 ? {
                  //           label: renderSelectedProperties(selectedProperties),
                  //           value: "properties"
                  //         } : null}
                  //         title="Property or properties"
                  //         onClick={() => setShowMultiSelectModal(true)}
                  //       />

                  //       <NewPackage
                  //         onClick={handleReplicateSettings}
                  //         disabled={isLoading || selectedProperties.length === 0 || editSettingsLoading || isPropertyChanging}
                  //       >
                  //         {editSettingsLoading ? "Replicating..." : `Replicate to ${selectedProperties.length} Properties`}
                  //       </NewPackage>


                  //     </PropertiesSelection>
                  //   </ReplicateContent>
                  // </ReplicateSection>

                  <ReplicateSection>

                    {/* Header */}
                    <ReplicateHeader>
                      <ReplicateTitle>
                        Apply Settings to Other Properties
                      </ReplicateTitle>
                      <ReplicateSubtitle>
                        Instantly copy these settings across selected properties
                      </ReplicateSubtitle>
                    </ReplicateHeader>

                    <ReplicateContent>

                      {/* Selection Card */}
                      <ReplicateSelectionCard
                        type="button"
                        onClick={() => setShowMultiSelectModal(true)}
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Select Properties
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {selectedProperties.length > 0
                              ? renderSelectedProperties(selectedProperties)
                              : "Choose one or more properties"}
                          </p>
                        </div>

                        <span className="text-primary-color text-sm font-medium">
                          {selectedProperties.length > 0 ? "Edit" : "Select"}
                        </span>
                      </ReplicateSelectionCard>

                      {/* CTA Button */}
                      <PrimaryAction
                        onClick={handleReplicateSettings}
                        disabled={isLoading || selectedProperties.length === 0 || editSettingsLoading}
                      >
                        {editSettingsLoading
                          ? "Applying..."
                          : selectedProperties.length > 0
                            ? `Apply to ${selectedProperties.length} Properties`
                            : "Select Properties First"}
                      </PrimaryAction>

                    </ReplicateContent>
                  </ReplicateSection>
                )}
              </Section>
            </ContentWrapper>

            {/* Single Property Selection Modal */}
            <SinglePropSelectModal
              isOpen={showPropertyModal}
              onClose={() => setShowPropertyModal(false)}
              onSelect={handleSelectedProperty}
              options={filteredProperties}
              selectedProperty={selectedProperty}
            />

            {/* Multi Property Selection Modal */}
            <MultiPropSelectModal
              isOpen={showMultiSelectModal}
              onClose={() => setShowMultiSelectModal(false)}
              onSelect={handleMultiPropertySave}
              options={filteredProperties}
              selectedProperties={selectedProperties}
            />
          </ContentContainer>
        </>
      )}
    </div>
  );
};

// Styled Components
const ContentContainer = tw.div`flex-1 overflow-y-auto`;
const ContentWrapper = tw.div`p-6`;
const Section = tw.div`space-y-6`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] p-4`;
const HeaderTitle = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button``;
const PropertySection = tw.div`bg-gray-50 rounded-xl p-4 border border-gray-200 mb-4`;
const PropertyDisplayContainer = tw.div`flex items-center gap-1 truncate`;
const RemainingCount = tw.span`text-gray-500 ml-1 font-medium`;
const SettingsGrid = tw.div`space-y-4`;
// const SettingCard = tw.div`bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-200`;
// const SettingContent = tw.div`flex items-start justify-between space-x-6 p-5`;
// const SettingHeader = tw.div`flex-1`;
// const SettingTitle = tw.h3`font-semibold text-gray-800 mb-2 text-base`;
// const SettingDescription = tw.p`text-sm text-gray-600 leading-relaxed`;

const Toggle = ({ checked, onChange, disabled }) => (
  <ToggleLabel disabled={disabled}>
    <ToggleInput
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <ToggleSlider checked={checked} disabled={disabled}>
      <ToggleThumb checked={checked} />
    </ToggleSlider>
  </ToggleLabel>
);

const ToggleLabel = tw.label`${props => props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} relative inline-flex items-center`;
const ToggleInput = tw.input`sr-only peer`;
// const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.disabled ? 'bg-gray-200 cursor-not-allowed' : props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
const ToggleThumb = tw.div`absolute top-0.5 left-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${props => props.checked ? 'transform translate-x-6' : ''}`;
const PropertiesSelection = tw.div`space-y-3`;
const NewPackage = tw.button`w-full border border-primary-color items-center mb-2 text-primary-color h-12 rounded-lg hover:bg-primary-color hover:text-white disabled:opacity-50 disabled:cursor-not-allowed`;


const SettingCard = tw.div`
  relative rounded-xl border p-4 transition-all duration-200 bg-white
  ${(p) =>
    p.$active
      ? "border-gray-300 shadow-sm"
      : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}
`;

const SettingContent = tw.div`
  flex items-center justify-between gap-4
`;

const SettingHeader = tw.div`flex-1`;

const TitleRow = tw.div`
  flex items-center gap-2 mb-1
`;

const SettingTitle = tw.h3`
  text-sm font-semibold text-gray-800
`;

const SettingDescription = tw.p`
  text-xs text-gray-500 leading-relaxed
`;

const ToggleWrapper = tw.div`
  flex items-center justify-center
`;

const StatusBadge = tw.span`
  text-[10px] font-medium px-2 py-0.5 rounded-full
  ${(p) =>
    p.$variant === "warning"
      ? "bg-yellow-100 text-yellow-700"
      : "bg-green-100 text-green-700"}
`;

const HelperText = tw.p`
  text-xs text-gray-400 mt-1
`;

export default OnboardingSettingsModal;